import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Sparkles, Loader2, Copy, Check,
  Linkedin, Twitter, Facebook, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SUBSCRIBE_URL = 'https://riddhi-responsibleai-corner.org/learn';

const PLATFORMS = [
  {
    key: 'linkedin',
    name: 'LinkedIn',
    icon: Linkedin,
    color: 'bg-[#0A66C2] hover:bg-[#0958AB]',
    field: 'linkedin_text',
    shareLabel: 'Open LinkedIn',
    // LinkedIn share intent doesn't reliably accept custom text; opens the share-with-link dialog.
    // The recommended UX: copy text → click Share → paste in the LinkedIn editor.
    buildShareUrl: () =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SUBSCRIBE_URL)}`,
  },
  {
    key: 'twitter',
    name: 'X / Twitter',
    icon: Twitter,
    color: 'bg-black hover:bg-gray-900',
    field: 'twitter_text',
    shareLabel: 'Post to X',
    buildShareUrl: (text) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
  },
  {
    key: 'facebook',
    name: 'Facebook',
    icon: Facebook,
    color: 'bg-[#1877F2] hover:bg-[#166FE5]',
    field: 'facebook_text',
    shareLabel: 'Open Facebook',
    buildShareUrl: (text) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SUBSCRIBE_URL)}&quote=${encodeURIComponent(text)}`,
  },
];

const AdminSocial = () => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copiedKey, setCopiedKey] = useState('');
  const [mode, setMode] = useState('lesson');
  const navigate = useNavigate();

  const headers = () => {
    const t = localStorage.getItem('admin_token');
    if (!t) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${t}` };
  };

  const load = async () => {
    setLoading(true);
    try {
      const h = headers();
      if (!h) return;
      const res = await axios.get(`${BACKEND_URL}/api/admin/social/today`, { headers: h });
      setPost(res.data?.post || null);
    } catch (e) {
      if (e.response?.status === 401) navigate('/admin/login');
      else toast.error('Failed to load social post');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleGenerate = async (force = false) => {
    setGenerating(true);
    try {
      const h = headers();
      if (!h) return;
      toast.info(mode === 'guide_marketing'
        ? 'Crafting a guide-promo post…'
        : 'Crafting today\'s lesson teaser…'
      );
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/social/generate`,
        { force, mode },
        { headers: h, timeout: 120000 }
      );
      setPost(res.data.post);
      toast.success(force ? 'Fresh post generated' : 'Post ready');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedKey(''), 2000);
    } catch {
      toast.error('Copy failed');
    }
  };

  const openShare = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="social-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/admin/dashboard" className="flex items-center gap-2 text-gray-600 hover:text-black">
            <ArrowLeft size={20} /><span>Back to Dashboard</span>
          </Link>
          <h1 className="text-2xl font-light">Daily Social Post</h1>
          <div />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Mode toggle */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="text-sm font-medium text-gray-700">Post type:</span>
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-sm" data-testid="social-mode-toggle">
            <button
              onClick={() => setMode('lesson')}
              className={`px-4 py-2 ${mode === 'lesson' ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              data-testid="social-mode-lesson"
            >
              Daily lesson teaser
            </button>
            <button
              onClick={() => setMode('guide_marketing')}
              className={`px-4 py-2 border-l border-gray-300 ${mode === 'guide_marketing' ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              data-testid="social-mode-guides"
            >
              Promote Free Guides (PDF)
            </button>
          </div>
          <p className="text-xs text-gray-500 sm:ml-auto">
            {mode === 'guide_marketing'
              ? 'Pitches the free PDF bundle, links to /guides-pdf (subscribe-gated download).'
              : 'Teases a fresh AI ethics lesson, links to /learn.'}
          </p>
        </div>

        {/* Top action bar */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-medium mb-1">
              {post ? `Today's post · ${post.date}` : 'No post yet for today'}
            </h2>
            <p className="text-sm text-gray-600">
              {post
                ? <>Hook: <span className="font-medium text-gray-900">"{post.hook}"</span>{post.lesson_title ? <> · {post.mode === 'guide_marketing' ? <em>Promotes</em> : 'Snippet from'} <span className="italic">{post.lesson_title}</span></> : null}</>
                : 'Pick a post type above, then click "Generate Today\'s Post".'}
            </p>
            {post?.mode === 'guide_marketing' ? (
              <p className="text-xs mt-2 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                Tip: pair this with the LinkedIn Carousel PDF (Admin → Generate PDF) for maximum reach.
              </p>
            ) : null}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => handleGenerate(false)}
              disabled={generating || (!!post && post.mode === mode)}
              className="flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
              data-testid="social-generate-btn"
            >
              {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              <span>{(post && post.mode === mode) ? 'Already generated' : 'Generate Today\'s Post'}</span>
            </button>
            <button
              onClick={() => handleGenerate(true)}
              disabled={generating}
              className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 text-sm"
              data-testid="social-regenerate-btn"
            >
              {generating ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              <span>Regenerate</span>
            </button>
          </div>
        </div>

        {!post ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-lg p-16 text-center">
            <Sparkles size={32} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-600">Generate today's post to see LinkedIn, X, and Facebook versions here.</p>
            <p className="text-sm text-gray-500 mt-2">A new post is also auto-generated every day at 08:30 UTC.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {PLATFORMS.map(p => {
              const text = post[p.field] || '';
              const Icon = p.icon;
              return (
                <div key={p.key} className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col" data-testid={`social-card-${p.key}`}>
                  <div className={`${p.color} text-white px-5 py-3 flex items-center justify-between`}>
                    <div className="flex items-center gap-2">
                      <Icon size={18} />
                      <span className="font-medium text-sm">{p.name}</span>
                    </div>
                    <span className="text-xs opacity-80">{text.length} chars</span>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed flex-1 min-h-[200px]" data-testid={`social-text-${p.key}`}>
                      {text}
                    </pre>
                    <div className="mt-4 flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => handleCopy(p.key, text)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                        data-testid={`social-copy-${p.key}`}
                      >
                        {copiedKey === p.key ? (
                          <><Check size={16} className="text-green-600" /><span>Copied</span></>
                        ) : (
                          <><Copy size={16} /><span>Copy text</span></>
                        )}
                      </button>
                      <button
                        onClick={() => openShare(p.buildShareUrl(text))}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 ${p.color} text-white rounded-md text-sm`}
                        data-testid={`social-share-${p.key}`}
                      >
                        <Icon size={16} />
                        <span>{p.shareLabel}</span>
                      </button>
                    </div>
                    {p.key === 'linkedin' && (
                      <p className="mt-2 text-xs text-gray-500">
                        LinkedIn doesn't accept pre-filled text. Click <span className="font-medium">Copy text</span>, then <span className="font-medium">Open LinkedIn</span> and paste in the editor.
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminSocial;
